class AnalyticsService {
  constructor() {
    this.measurementId = import.meta.env?.VITE_GOOGLE_ANALYTICS_ID;
    this.enabled = this.measurementId && this.measurementId !== 'your-google-analytics-id-here';
  }

  // Check if gtag is available
  isAvailable() {
    return this.enabled && typeof window.gtag !== 'undefined';
  }

  // Track custom events
  trackEvent(eventName, parameters = {}) {
    if (!this.isAvailable()) return;

    window.gtag('event', eventName, {
      ...parameters,
      timestamp: new Date()?.toISOString(),
    });
  }

  // Conversion Tracking
  trackConversion(conversionType, value = 0, parameters = {}) {
    if (!this.isAvailable()) return;

    window.gtag('event', 'conversion', {
      conversion_type: conversionType,
      value: value,
      currency: 'USD',
      ...parameters,
    });
  }

  // User Registration Conversion
  trackRegistration(userType, userId) {
    this.trackConversion('registration', 0, {
      user_type: userType,
      user_id: userId,
      method: 'email',
    });

    this.trackEvent('sign_up', {
      method: 'email',
      user_type: userType,
    });
  }

  // Social Auth Registration
  trackSocialRegistration(provider, userType, userId) {
    this.trackConversion('registration', 0, {
      user_type: userType,
      user_id: userId,
      method: provider,
    });

    this.trackEvent('sign_up', {
      method: provider,
      user_type: userType,
    });
  }

  // Job Application Conversion
  trackJobApplication(jobId, jobTitle, companyName, applicationMethod) {
    this.trackConversion('job_application', 0, {
      job_id: jobId,
      job_title: jobTitle,
      company_name: companyName,
      application_method: applicationMethod,
    });

    this.trackEvent('apply_job', {
      job_id: jobId,
      job_title: jobTitle,
      company_name: companyName,
      method: applicationMethod,
    });
  }

  // Subscription Conversion
  trackSubscription(planName, planPrice, billingCycle, userId) {
    this.trackConversion('subscription', planPrice, {
      plan_name: planName,
      billing_cycle: billingCycle,
      user_id: userId,
    });

    this.trackEvent('purchase', {
      transaction_id: `sub_${Date.now()}`,
      value: planPrice,
      currency: 'USD',
      items: [{
        item_id: planName,
        item_name: planName,
        item_category: 'subscription',
        price: planPrice,
        quantity: 1,
      }],
    });
  }

  // Feature Usage Tracking
  trackFeatureUsage(featureName, action, parameters = {}) {
    this.trackEvent('feature_usage', {
      feature_name: featureName,
      action: action,
      ...parameters,
    });
  }

  // Job Search Tracking
  trackJobSearch(query, filters, resultsCount) {
    this.trackEvent('search', {
      search_term: query,
      filters: JSON.stringify(filters),
      results_count: resultsCount,
    });
  }

  // Job View Tracking
  trackJobView(jobId, jobTitle, companyName, source) {
    this.trackEvent('view_item', {
      item_id: jobId,
      item_name: jobTitle,
      item_category: 'job',
      company_name: companyName,
      source: source,
    });
  }

  // Job Save Tracking
  trackJobSave(jobId, jobTitle, companyName) {
    this.trackEvent('add_to_wishlist', {
      item_id: jobId,
      item_name: jobTitle,
      item_category: 'job',
      company_name: companyName,
    });
  }

  // Profile Completion Tracking
  trackProfileCompletion(completionPercentage, section) {
    this.trackEvent('profile_completion', {
      completion_percentage: completionPercentage,
      section: section,
    });
  }

  // Onboarding Progress Tracking
  trackOnboardingStep(step, stepName, userType) {
    this.trackEvent('onboarding_progress', {
      step: step,
      step_name: stepName,
      user_type: userType,
    });
  }

  // Company Profile View
  trackCompanyView(companyId, companyName, source) {
    this.trackEvent('view_company', {
      company_id: companyId,
      company_name: companyName,
      source: source,
    });
  }

  // Job Posting Creation
  trackJobPosting(jobId, jobTitle, method) {
    this.trackEvent('create_job_posting', {
      job_id: jobId,
      job_title: jobTitle,
      creation_method: method,
    });
  }

  // Candidate Unlock
  trackCandidateUnlock(candidateId, unlockMethod, cost) {
    this.trackConversion('candidate_unlock', cost, {
      candidate_id: candidateId,
      unlock_method: unlockMethod,
    });

    this.trackEvent('unlock_candidate', {
      candidate_id: candidateId,
      method: unlockMethod,
      cost: cost,
    });
  }

  // User Login
  trackLogin(method, userType) {
    this.trackEvent('login', {
      method: method,
      user_type: userType,
    });
  }

  // User Logout
  trackLogout(userType) {
    this.trackEvent('logout', {
      user_type: userType,
    });
  }

  // Error Tracking
  trackError(errorType, errorMessage, page) {
    this.trackEvent('error', {
      error_type: errorType,
      error_message: errorMessage,
      page: page,
    });
  }

  // Performance Tracking
  trackPerformance(metricName, value, page) {
    this.trackEvent('performance_metric', {
      metric_name: metricName,
      value: value,
      page: page,
    });
  }

  // User Engagement
  trackEngagement(action, duration, page) {
    this.trackEvent('user_engagement', {
      action: action,
      duration_seconds: duration,
      page: page,
    });
  }

  // Set user properties
  setUserProperties(userId, userType, properties = {}) {
    if (!this.isAvailable()) return;

    window.gtag('set', 'user_properties', {
      user_id: userId,
      user_type: userType,
      ...properties,
    });
  }

  // Set user ID
  setUserId(userId) {
    if (!this.isAvailable()) return;

    window.gtag('config', this.measurementId, {
      user_id: userId,
    });
  }
}

export default new AnalyticsService();